// Phase HR-003 Wave 5 — Floor walk: Safety / Fatigue / Ergonomic capture.
//
// One page, three tabs. Same chip-based capture pattern as Phase HR-002.
// Operator walks the floor with a phone, taps a tab, taps chips, takes a
// photo, types a one-liner, hits Save. ~15 seconds per observation.
//
// Doc Studies served: #33 Fatigue, #35 Ergonomic, #40 Unsafe Act,
//                     #41 PPE Compliance, #43 Near-miss, plus
//                     Good Practice (positive reinforcement).

import { useMemo, useState } from 'react'
import {
  Alert, App, Button, Card, DatePicker, Empty, Form, Input, InputNumber,
  List, Modal, Radio, Select, Space, Switch, Tabs, Tag, Typography, Upload,
} from 'antd'
import {
  CameraOutlined, CheckCircleOutlined, EnvironmentOutlined,
  FireOutlined, PlusOutlined, SafetyOutlined, ToolOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs, { type Dayjs } from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import {
  ehsApi, type FatigueSign, type PostureType, type RiskBand,
  type RiskMethod, type SafetyCategory, type SafetyObsType,
  type SeverityPotential,
} from '@/features/hr/ehsApi'
import { departmentsApi } from '@/features/masters/departments/api'
import PageHeader from '@/shared/components/PageHeader'
import { usePlantId } from '@/shared/hooks/usePlantId'
import { useHrMobilePolish, type ThresholdResult } from '@/shared/styles/hrMobilePolish'

dayjs.extend(relativeTime)
const { Text } = Typography

// ─── Chip palettes ─────────────────────────────────────────────────

const FATIGUE_SIGN_CHIPS: { code: FatigueSign; label: string }[] = [
  { code: 'SLOW_LIFTING',      label: 'Slow lifting' },
  { code: 'PAUSING',           label: 'Pausing' },
  { code: 'YAWNING',           label: 'Yawning' },
  { code: 'SWEATING',          label: 'Sweating' },
  { code: 'COMPLAINT_VERBAL',  label: 'Verbal complaint' },
  { code: 'POSTURE_DROP',      label: 'Posture drop' },
  { code: 'ERRORS_RISING',     label: 'Errors rising' },
]

const POSTURE_CHIPS: { code: PostureType; label: string }[] = [
  { code: 'STANDING',  label: 'Standing' },
  { code: 'BENT',      label: 'Bent' },
  { code: 'TWISTED',   label: 'Twisted' },
  { code: 'OVERHEAD',  label: 'Overhead' },
  { code: 'SEATED',    label: 'Seated' },
  { code: 'SQUATTING', label: 'Squat' },
]

const RISK_BAND_OPTIONS: { value: RiskBand; label: string; color: string }[] = [
  { value: 'LOW',       label: 'Low',       color: 'green' },
  { value: 'MEDIUM',    label: 'Medium',    color: 'gold' },
  { value: 'HIGH',      label: 'High',      color: 'orange' },
  { value: 'VERY_HIGH', label: 'Very high', color: 'red' },
]

const SAFETY_TYPE_CHIPS: { code: SafetyObsType; label: string; icon: React.ReactNode; color: string }[] = [
  { code: 'UNSAFE_ACT',         label: 'Unsafe act',  icon: <WarningOutlined />,     color: 'red' },
  { code: 'PPE_NON_COMPLIANCE', label: 'PPE missing', icon: <SafetyOutlined />,      color: 'orange' },
  { code: 'NEAR_MISS',          label: 'Near miss',   icon: <FireOutlined />,        color: 'volcano' },
  { code: 'GOOD_PRACTICE',      label: 'Good!',       icon: <CheckCircleOutlined />, color: 'green' },
]

const SAFETY_CAT_CHIPS: { code: SafetyCategory; label: string }[] = [
  // Hazards / behaviours
  { code: 'FALL',             label: 'Fall' },
  { code: 'LIFT',             label: 'Lifting' },
  { code: 'ROTATING_MACHINE', label: 'Rotating m/c' },
  { code: 'CHEMICAL',         label: 'Chemical' },
  { code: 'NOISE',            label: 'Noise' },
  { code: 'HEAT',             label: 'Heat' },
  { code: 'ELECTRICAL',       label: 'Electrical' },
  { code: 'TRIP_SLIP',        label: 'Trip / slip' },
  // PPE
  { code: 'HELMET',           label: 'Helmet' },
  { code: 'GOGGLES',          label: 'Goggles' },
  { code: 'SHOES',            label: 'Shoes' },
  { code: 'GLOVES',           label: 'Gloves' },
  { code: 'EAR_PLUG',         label: 'Ear plug' },
  { code: 'MASK',             label: 'Mask' },
  // Misc
  { code: 'HOUSEKEEPING',     label: 'Housekeeping' },
  { code: 'EMERGENCY_EXIT',   label: 'Exit blocked' },
  { code: 'TOOL_HANDLING',    label: 'Tool use' },
  { code: 'OTHER',            label: 'Other' },
]

const SEVERITY_CHIPS: { code: SeverityPotential; label: string; color: string }[] = [
  { code: 'LOW',    label: 'Low',    color: 'default' },
  { code: 'MEDIUM', label: 'Medium', color: 'blue' },
  { code: 'HIGH',   label: 'High',   color: 'orange' },
  { code: 'FATAL',  label: 'Fatal',  color: 'red' },
]

// ─── Helpers ───────────────────────────────────────────────────────

function severityToTile(sev: SeverityPotential | null | undefined): ThresholdResult {
  if (!sev) return 'neutral'
  if (sev === 'FATAL' || sev === 'HIGH') return 'red'
  if (sev === 'MEDIUM') return 'amber'
  return 'green'
}

function riskBandToColor(b: RiskBand | null | undefined): string {
  if (!b) return 'default'
  return RISK_BAND_OPTIONS.find((o) => o.value === b)?.color ?? 'default'
}

// ─── Main page ─────────────────────────────────────────────────────

export default function SafetyWalkPage() {
  useHrMobilePolish()
  const { plantId } = usePlantId()
  const [tab, setTab] = useState<'safety' | 'fatigue' | 'ergonomic'>('safety')

  return (
    <div className="hr-mobile-root">
      <PageHeader
        title="EHS Floor Walk"
        subtitle="Doc §33/35/40/41/43 — capture in 15 seconds while walking the floor."
      />
      {!plantId && (
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message="Pick a plant in the top bar before capturing." />
      )}
      <Tabs
        activeKey={tab}
        onChange={(k) => setTab(k as typeof tab)}
        items={[
          { key: 'safety',    label: <span><SafetyOutlined /> Safety</span>,
            children: <SafetyTab /> },
          { key: 'fatigue',   label: <span><FireOutlined /> Fatigue</span>,
            children: <FatigueTab /> },
          { key: 'ergonomic', label: <span><ToolOutlined /> Ergonomic</span>,
            children: <ErgonomicTab /> },
        ]}
      />
    </div>
  )
}

// ═══════════════════════ Safety tab ═════════════════════════════════

function SafetyTab() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const [openCapture, setOpenCapture] = useState(false)
  const [typeFilter, setTypeFilter] = useState<SafetyObsType | 'ALL'>('ALL')
  const [openOnly, setOpenOnly] = useState(true)

  const { data: deptsData } = useQuery({
    queryKey: ['safety-depts', plantId],
    queryFn: () => departmentsApi.list({ page: 1, page_size: 500, plant_id: plantId }),
    enabled: !!plantId,
  })

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['safety-list', plantId, typeFilter, openOnly],
    queryFn: () => ehsApi.safetyList({
      plant_id: plantId,
      observation_type: typeFilter === 'ALL' ? undefined : typeFilter,
      open_only: openOnly,
      limit: 200,
    }),
    enabled: !!plantId,
  })

  const { data: stats } = useQuery({
    queryKey: ['safety-stats', plantId],
    queryFn: () => ehsApi.safetyStats({ plant_id: plantId }),
    enabled: !!plantId,
  })

  const refresh = () => qc.invalidateQueries({ queryKey: ['safety-list'] })

  return (
    <>
      {/* KPI tiles */}
      <div style={{
        display: 'grid', gap: 8, marginBottom: 12,
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
      }}>
        <KPITile variant="neutral" value={stats?.total ?? 0} label="Total" />
        <KPITile
          variant={(stats?.open_count ?? 0) === 0 ? 'green' : 'amber'}
          value={stats?.open_count ?? 0} label="Open" />
        <KPITile
          variant={(stats?.overdue_count ?? 0) > 0 ? 'red' : 'green'}
          value={stats?.overdue_count ?? 0} label="Overdue" />
        <KPITile
          variant="neutral"
          value={stats?.avg_closure_hours == null ? '—' : `${stats.avg_closure_hours.toFixed(1)}h`}
          label="Avg closure" />
      </div>

      {/* Filter chips + new */}
      <Space wrap style={{ marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />}
          onClick={() => setOpenCapture(true)}>
          New observation
        </Button>
        <div className="hr-filter-chips">
          {([
            { v: 'ALL', label: 'All' },
            { v: 'UNSAFE_ACT', label: 'Unsafe' },
            { v: 'PPE_NON_COMPLIANCE', label: 'PPE' },
            { v: 'NEAR_MISS', label: 'Near miss' },
            { v: 'GOOD_PRACTICE', label: 'Good' },
          ] as const).map(({ v, label }) => (
            <div key={v} role="button"
                 className={`hr-filter-chip${typeFilter === v ? ' hr-filter-chip--active' : ''}`}
                 onClick={() => setTypeFilter(v)}>
              {label}
            </div>
          ))}
        </div>
        <Space>
          <Switch checked={openOnly} onChange={setOpenOnly} />
          <Text style={{ fontSize: 12 }}>Open only</Text>
        </Space>
      </Space>

      {isLoading ? <Empty description="Loading…" image={Empty.PRESENTED_IMAGE_SIMPLE} />
       : rows.length === 0 ? <Empty description="No observations." image={Empty.PRESENTED_IMAGE_SIMPLE} />
       : (
        <div style={{
          display: 'grid', gap: 8,
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        }}>
          {rows.map((r) => <SafetyCard key={r.id} row={r} onClosed={refresh} />)}
        </div>
      )}

      <SafetyCaptureModal
        open={openCapture}
        onClose={() => setOpenCapture(false)}
        onSaved={refresh}
        departments={(deptsData?.items ?? []).map((d) => ({ value: d.id, label: d.name }))}
      />
    </>
  )
}

function SafetyCard({ row, onClosed }: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  row: any; onClosed: () => void
}) {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [closing, setClosing] = useState(false)
  const [notes, setNotes] = useState('')

  const closeMut = useMutation({
    mutationFn: () => ehsApi.safetyClose(row.id, notes),
    onSuccess: () => {
      message.success('Closed.')
      qc.invalidateQueries({ queryKey: ['safety-list'] })
      qc.invalidateQueries({ queryKey: ['safety-stats'] })
      setClosing(false); setNotes('')
      onClosed()
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Close failed'),
  })

  const sev = row.severity_potential as SeverityPotential | null
  const tile = severityToTile(sev)
  const tilecolor = tile === 'red' ? '#cf1322' : tile === 'amber' ? '#d48806'
                  : tile === 'green' ? '#389e0d' : undefined
  const typeMeta = SAFETY_TYPE_CHIPS.find((t) => t.code === row.observation_type)
  const isClosed = !!row.action_closed_at

  return (
    <div className="hr-roster-card" style={{
      flexDirection: 'column', alignItems: 'stretch',
      borderLeft: tilecolor ? `4px solid ${tilecolor}` : undefined,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {typeMeta && (
          <Tag color={typeMeta.color} icon={typeMeta.icon}>{typeMeta.label}</Tag>
        )}
        {row.category_code && <Tag>{row.category_code}</Tag>}
        {sev && <Tag color={SEVERITY_CHIPS.find((s) => s.code === sev)?.color}>{sev}</Tag>}
        {isClosed && <Tag color="green">CLOSED</Tag>}
      </div>
      <div style={{ marginTop: 6 }}>
        <Text>{row.description}</Text>
      </div>
      {row.photo_url && (
        <div style={{ marginTop: 6 }}>
          <img src={row.photo_url} alt="evidence"
               style={{ maxWidth: '100%', maxHeight: 160,
                        borderRadius: 6, border: '1px solid #f0f0f0' }} />
        </div>
      )}
      <div style={{ marginTop: 6, fontSize: 11, color: '#8c8c8c' }}>
        {row.department_name && <>{row.department_name}</>}
        {row.machine_code && <> · {row.machine_code}</>}
        <> · {dayjs(row.observation_at).fromNow()}</>
      </div>
      {row.action_closure_notes && (
        <div style={{ marginTop: 6, fontSize: 12, color: '#389e0d' }}>
          <CheckCircleOutlined /> {row.action_closure_notes}
        </div>
      )}
      {!isClosed && (
        <div style={{ marginTop: 8 }}>
          {!closing ? (
            <Button size="small" type="primary" icon={<CheckCircleOutlined />}
              onClick={() => setClosing(true)}>Close action</Button>
          ) : (
            <Space.Compact style={{ width: '100%' }}>
              <Input placeholder="Closure note"
                     value={notes} onChange={(e) => setNotes(e.target.value)} />
              <Button type="primary" loading={closeMut.isPending}
                disabled={!notes.trim()}
                onClick={() => closeMut.mutate()}>Save</Button>
              <Button onClick={() => { setClosing(false); setNotes('') }}>Cancel</Button>
            </Space.Compact>
          )}
        </div>
      )}
    </div>
  )
}

function SafetyCaptureModal({ open, onClose, onSaved, departments }: {
  open: boolean; onClose: () => void; onSaved: () => void
  departments: { value: string; label: string }[]
}) {
  const { message } = App.useApp()
  const { plantId } = usePlantId()
  const [type, setType] = useState<SafetyObsType>('UNSAFE_ACT')
  const [category, setCategory] = useState<SafetyCategory | null>(null)
  const [severity, setSeverity] = useState<SeverityPotential | null>('MEDIUM')
  const [deptId, setDeptId] = useState<string | undefined>()
  const [desc, setDesc] = useState('')
  const [immediate, setImmediate] = useState('')
  const [proposed, setProposed] = useState('')
  const [photoUrl, setPhotoUrl] = useState<string | null>(null)

  // Quickly reset when modal opens
  useMemo(() => {
    if (open) {
      setType('UNSAFE_ACT'); setCategory(null); setSeverity('MEDIUM')
      setDeptId(undefined); setDesc(''); setImmediate(''); setProposed('')
      setPhotoUrl(null)
    }
  }, [open])

  const saveMut = useMutation({
    mutationFn: () => {
      if (!plantId) throw new Error('Plant context missing')
      return ehsApi.safetyCreate({
        plant_id: plantId,
        observation_type: type,
        category_code: category,
        severity_potential: severity,
        department_id: deptId || null,
        description: desc,
        immediate_action_taken: immediate || null,
        proposed_action: proposed || null,
        photo_url: photoUrl || null,
      })
    },
    onSuccess: () => {
      message.success('Observation saved.')
      onSaved(); onClose()
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => message.error(
      e?.response?.data?.detail ?? e?.message ?? 'Save failed'),
  })

  return (
    <Modal
      open={open} onCancel={onClose}
      title="New safety observation"
      width={520}
      confirmLoading={saveMut.isPending}
      onOk={() => saveMut.mutate()}
      okButtonProps={{ disabled: desc.trim().length < 3 }}
      okText="Save"
    >
      {/* Type chip strip */}
      <Text strong style={{ fontSize: 12 }}>Type</Text>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', margin: '6px 0 12px' }}>
        {SAFETY_TYPE_CHIPS.map((t) => (
          <button key={t.code} type="button"
            className={`hr-filter-chip${type === t.code ? ' hr-filter-chip--active' : ''}`}
            onClick={() => setType(t.code)}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Category chip strip */}
      <Text strong style={{ fontSize: 12 }}>Category</Text>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', margin: '6px 0 12px' }}>
        {SAFETY_CAT_CHIPS.map((c) => (
          <button key={c.code} type="button"
            className={`hr-filter-chip${category === c.code ? ' hr-filter-chip--active' : ''}`}
            style={{ height: 28, fontSize: 11 }}
            onClick={() => setCategory(category === c.code ? null : c.code)}>
            {c.label}
          </button>
        ))}
      </div>

      {/* Severity chip strip (skip for good-practice) */}
      {type !== 'GOOD_PRACTICE' && (
        <>
          <Text strong style={{ fontSize: 12 }}>Severity potential</Text>
          <div style={{ display: 'flex', gap: 4, marginTop: 6, marginBottom: 12 }}>
            {SEVERITY_CHIPS.map((s) => (
              <button key={s.code} type="button"
                className={`hr-filter-chip${severity === s.code ? ' hr-filter-chip--active' : ''}`}
                onClick={() => setSeverity(s.code)}>{s.label}</button>
            ))}
          </div>
        </>
      )}

      <Form layout="vertical">
        <Form.Item label="Department">
          <Select allowClear placeholder="Pick department"
            value={deptId} onChange={setDeptId} options={departments} />
        </Form.Item>
        <Form.Item label="Description" required>
          <Input.TextArea rows={3} value={desc} onChange={(e) => setDesc(e.target.value)}
            placeholder="What did you see? (≥3 chars)" />
        </Form.Item>
        <Form.Item label="Immediate action taken">
          <Input value={immediate} onChange={(e) => setImmediate(e.target.value)}
            placeholder="What you did right away (optional)" />
        </Form.Item>
        <Form.Item label="Proposed follow-up">
          <Input value={proposed} onChange={(e) => setProposed(e.target.value)}
            placeholder="What still needs doing (optional)" />
        </Form.Item>
        <Form.Item label="Photo (optional)">
          {/* Upload uses the existing /static evidence path. For now just
              accept a URL — wire to the same evidence-upload endpoint as
              Phase HR-002 in a follow-up. */}
          <Input value={photoUrl ?? ''} onChange={(e) => setPhotoUrl(e.target.value || null)}
            placeholder="Photo URL (paste after upload)" />
        </Form.Item>
      </Form>
    </Modal>
  )
}

// ═══════════════════════ Fatigue tab ════════════════════════════════

function FatigueTab() {
  const { plantId } = usePlantId()
  const [openCapture, setOpenCapture] = useState(false)
  const qc = useQueryClient()
  const refresh = () => qc.invalidateQueries({ queryKey: ['fatigue-list'] })

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['fatigue-list', plantId],
    queryFn: () => ehsApi.fatigueList({ plant_id: plantId, limit: 200 }),
    enabled: !!plantId,
  })

  return (
    <>
      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />}
          onClick={() => setOpenCapture(true)}>
          New fatigue observation
        </Button>
      </Space>

      {isLoading ? <Empty description="Loading…" image={Empty.PRESENTED_IMAGE_SIMPLE} />
       : rows.length === 0 ? <Empty description="No fatigue observations yet." image={Empty.PRESENTED_IMAGE_SIMPLE} />
       : (
        <List dataSource={rows} renderItem={(r) => (
          <List.Item>
            <List.Item.Meta
              title={<Space wrap>
                <Text strong>{r.hour_band ?? '—'}</Text>
                <Tag>{r.department_name}</Tag>
                {r.ot_status && <Tag color="orange">OT</Tag>}
                {(r.fatigue_signs ?? []).map((s) => <Tag key={s}>{s}</Tag>)}
              </Space>}
              description={
                <Space direction="vertical" size={2}>
                  <Text style={{ fontSize: 12, color: '#8c8c8c' }}>
                    {dayjs(r.observation_date).format('DD MMM YYYY')}
                    {r.output_observed != null && <> · output {r.output_observed} {r.output_unit ?? 'kg'}/h</>}
                    {r.temperature_c != null && <> · {r.temperature_c}°C</>}
                    {r.humidity_pct != null && <> · RH {r.humidity_pct}%</>}
                    {r.noise_db != null && <> · {r.noise_db} dB</>}
                  </Text>
                  {r.worker_complaints && (
                    <Text style={{ fontSize: 12 }}>💬 {r.worker_complaints}</Text>
                  )}
                  {r.proposed_action && (
                    <Text style={{ fontSize: 12, color: '#1677ff' }}>→ {r.proposed_action}</Text>
                  )}
                </Space>
              }
            />
          </List.Item>
        )} />
       )}

      <FatigueCaptureModal open={openCapture} onClose={() => setOpenCapture(false)}
        onSaved={refresh} />
    </>
  )
}

function FatigueCaptureModal({ open, onClose, onSaved }: {
  open: boolean; onClose: () => void; onSaved: () => void
}) {
  const { message } = App.useApp()
  const { plantId } = usePlantId()
  const [obsDate, setObsDate] = useState<Dayjs>(dayjs())
  const [deptId, setDeptId] = useState<string | undefined>()
  const [hourBand, setHourBand] = useState('')
  const [signs, setSigns] = useState<Set<FatigueSign>>(new Set())
  const [temp, setTemp] = useState<number | null>(null)
  const [humidity, setHumidity] = useState<number | null>(null)
  const [noise, setNoise] = useState<number | null>(null)
  const [ot, setOt] = useState(false)
  const [output, setOutput] = useState<number | null>(null)
  const [outputUnit, setOutputUnit] = useState('kg/h')
  const [complaints, setComplaints] = useState('')
  const [proposed, setProposed] = useState('')

  const { data: deptsData } = useQuery({
    queryKey: ['fat-depts', plantId],
    queryFn: () => departmentsApi.list({ page: 1, page_size: 500, plant_id: plantId }),
    enabled: open && !!plantId,
  })

  useMemo(() => {
    if (open) {
      setObsDate(dayjs()); setDeptId(undefined); setHourBand('')
      setSigns(new Set()); setTemp(null); setHumidity(null); setNoise(null)
      setOt(false); setOutput(null); setOutputUnit('kg/h')
      setComplaints(''); setProposed('')
    }
  }, [open])

  const toggleSign = (s: FatigueSign) => {
    setSigns((prev) => {
      const next = new Set(prev)
      if (next.has(s)) next.delete(s); else next.add(s)
      return next
    })
  }

  const saveMut = useMutation({
    mutationFn: () => {
      if (!plantId || !deptId) throw new Error('Pick plant + department first')
      return ehsApi.fatigueCreate({
        plant_id: plantId, department_id: deptId,
        observation_date: obsDate.format('YYYY-MM-DD'),
        hour_band: hourBand || null,
        fatigue_signs: Array.from(signs),
        output_observed: output,
        output_unit: outputUnit || null,
        temperature_c: temp,
        humidity_pct: humidity,
        noise_db: noise,
        ot_status: ot,
        worker_complaints: complaints || null,
        proposed_action: proposed || null,
      })
    },
    onSuccess: () => {
      message.success('Fatigue observation saved.')
      onSaved(); onClose()
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => message.error(
      e?.response?.data?.detail ?? e?.message ?? 'Save failed'),
  })

  return (
    <Modal open={open} onCancel={onClose}
      title="Fatigue observation"
      width={520}
      onOk={() => saveMut.mutate()}
      confirmLoading={saveMut.isPending}
      okText="Save">
      <Form layout="vertical">
        <Space style={{ width: '100%' }}>
          <DatePicker value={obsDate} allowClear={false}
            onChange={(d) => d && setObsDate(d)} />
          <Select allowClear placeholder="Department" style={{ minWidth: 180 }}
            value={deptId} onChange={setDeptId}
            options={(deptsData?.items ?? []).map((d) => ({ label: d.name, value: d.id }))} />
          <Input placeholder="16:00-17:00" value={hourBand}
            onChange={(e) => setHourBand(e.target.value)} style={{ width: 130 }} />
        </Space>

        <div style={{ marginTop: 12 }}>
          <Text strong style={{ fontSize: 12 }}>Fatigue signs (tap any)</Text>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
            {FATIGUE_SIGN_CHIPS.map((s) => (
              <button key={s.code} type="button"
                className={`hr-filter-chip${signs.has(s.code) ? ' hr-filter-chip--active' : ''}`}
                onClick={() => toggleSign(s.code)}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{
          display: 'grid', gap: 8, marginTop: 12,
          gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
        }}>
          <NumField label="Output" value={output} onChange={setOutput} suffix={outputUnit} />
          <NumField label="Temp °C" value={temp} onChange={setTemp} />
          <NumField label="RH %" value={humidity} onChange={setHumidity} />
          <NumField label="Noise dB" value={noise} onChange={setNoise} />
        </div>

        <Space style={{ marginTop: 12 }}>
          <Switch checked={ot} onChange={setOt} />
          <Text>Worker is on OT</Text>
        </Space>

        <Form.Item label="Worker complaints" style={{ marginTop: 12 }}>
          <Input.TextArea rows={2} value={complaints}
            onChange={(e) => setComplaints(e.target.value)}
            placeholder="What the worker said" />
        </Form.Item>
        <Form.Item label="Proposed action">
          <Input.TextArea rows={2} value={proposed}
            onChange={(e) => setProposed(e.target.value)}
            placeholder="What to fix (rest pause, layout, OT cap…)" />
        </Form.Item>
      </Form>
    </Modal>
  )
}

// ═══════════════════════ Ergonomic tab ══════════════════════════════

function ErgonomicTab() {
  const { plantId } = usePlantId()
  const [openCapture, setOpenCapture] = useState(false)
  const qc = useQueryClient()
  const refresh = () => qc.invalidateQueries({ queryKey: ['ergo-list'] })

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['ergo-list', plantId],
    queryFn: () => ehsApi.ergonomicList({ plant_id: plantId, limit: 200 }),
    enabled: !!plantId,
  })

  return (
    <>
      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />}
          onClick={() => setOpenCapture(true)}>
          New ergonomic observation
        </Button>
      </Space>

      {isLoading ? <Empty description="Loading…" image={Empty.PRESENTED_IMAGE_SIMPLE} />
       : rows.length === 0 ? <Empty description="No ergonomic observations yet." image={Empty.PRESENTED_IMAGE_SIMPLE} />
       : (
        <List dataSource={rows} renderItem={(r) => (
          <List.Item>
            <List.Item.Meta
              title={<Space wrap>
                <Text strong>{r.task_description}</Text>
                {r.risk_band && <Tag color={riskBandToColor(r.risk_band)}>{r.risk_band}</Tag>}
                {r.posture_observed && <Tag>{r.posture_observed}</Tag>}
                {r.implemented_date && <Tag color="green">IMPLEMENTED</Tag>}
              </Space>}
              description={
                <Space direction="vertical" size={2}>
                  <Text style={{ fontSize: 12, color: '#8c8c8c' }}>
                    {dayjs(r.observation_date).format('DD MMM YYYY')}
                    {r.department_name && <> · {r.department_name}</>}
                    {r.machine_code && <> · {r.machine_code}</>}
                    {r.risk_score != null && <> · score {r.risk_score} ({r.risk_method})</>}
                  </Text>
                  {r.lift_weight_kg != null && (
                    <Text style={{ fontSize: 12 }}>Lift {r.lift_weight_kg} kg
                      {r.lift_frequency_per_hr != null && <> × {r.lift_frequency_per_hr}/h</>}
                    </Text>
                  )}
                  {r.proposed_action && (
                    <Text style={{ fontSize: 12, color: '#1677ff' }}>→ {r.proposed_action}</Text>
                  )}
                </Space>
              }
            />
          </List.Item>
        )} />
       )}

      <ErgonomicCaptureModal open={openCapture} onClose={() => setOpenCapture(false)}
        onSaved={refresh} />
    </>
  )
}

function ErgonomicCaptureModal({ open, onClose, onSaved }: {
  open: boolean; onClose: () => void; onSaved: () => void
}) {
  const { message } = App.useApp()
  const { plantId } = usePlantId()
  const [obsDate, setObsDate] = useState<Dayjs>(dayjs())
  const [deptId, setDeptId] = useState<string | undefined>()
  const [task, setTask] = useState('')
  const [posture, setPosture] = useState<PostureType | null>(null)
  const [liftWeight, setLiftWeight] = useState<number | null>(null)
  const [liftFreq, setLiftFreq] = useState<number | null>(null)
  const [reach, setReach] = useState<number | null>(null)
  const [seatedPct, setSeatedPct] = useState<number | null>(null)
  const [method, setMethod] = useState<RiskMethod>('SIMPLE')
  const [score, setScore] = useState<number | null>(null)
  const [band, setBand] = useState<RiskBand | null>(null)
  const [proposed, setProposed] = useState('')
  const [photoUrl, setPhotoUrl] = useState<string | null>(null)

  const { data: deptsData } = useQuery({
    queryKey: ['ergo-depts', plantId],
    queryFn: () => departmentsApi.list({ page: 1, page_size: 500, plant_id: plantId }),
    enabled: open && !!plantId,
  })

  useMemo(() => {
    if (open) {
      setObsDate(dayjs()); setDeptId(undefined); setTask('')
      setPosture(null); setLiftWeight(null); setLiftFreq(null)
      setReach(null); setSeatedPct(null)
      setMethod('SIMPLE'); setScore(null); setBand(null)
      setProposed(''); setPhotoUrl(null)
    }
  }, [open])

  const saveMut = useMutation({
    mutationFn: () => {
      if (!plantId || !deptId) throw new Error('Pick plant + department first')
      if (!task.trim()) throw new Error('Task description is required')
      return ehsApi.ergonomicCreate({
        plant_id: plantId, department_id: deptId,
        observation_date: obsDate.format('YYYY-MM-DD'),
        task_description: task,
        posture_observed: posture,
        lift_weight_kg: liftWeight,
        lift_frequency_per_hr: liftFreq,
        reach_distance_cm: reach,
        seated_pct: seatedPct,
        risk_method: method,
        risk_score: score,
        risk_band: band,
        proposed_action: proposed || null,
        photo_evidence_url: photoUrl || null,
      })
    },
    onSuccess: () => {
      message.success('Ergonomic observation saved.')
      onSaved(); onClose()
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => message.error(
      e?.response?.data?.detail ?? e?.message ?? 'Save failed'),
  })

  return (
    <Modal open={open} onCancel={onClose}
      title="Ergonomic observation"
      width={520}
      onOk={() => saveMut.mutate()}
      confirmLoading={saveMut.isPending}
      okText="Save">
      <Form layout="vertical">
        <Space style={{ width: '100%' }}>
          <DatePicker value={obsDate} allowClear={false}
            onChange={(d) => d && setObsDate(d)} />
          <Select allowClear placeholder="Department" style={{ minWidth: 180 }}
            value={deptId} onChange={setDeptId}
            options={(deptsData?.items ?? []).map((d) => ({ label: d.name, value: d.id }))} />
        </Space>

        <Form.Item label="Task description" required style={{ marginTop: 12 }}>
          <Input value={task} onChange={(e) => setTask(e.target.value)}
            placeholder="e.g. Cone packing from floor-level cartons" />
        </Form.Item>

        <Text strong style={{ fontSize: 12 }}>Posture</Text>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', margin: '6px 0 12px' }}>
          {POSTURE_CHIPS.map((p) => (
            <button key={p.code} type="button"
              className={`hr-filter-chip${posture === p.code ? ' hr-filter-chip--active' : ''}`}
              onClick={() => setPosture(posture === p.code ? null : p.code)}>
              {p.label}
            </button>
          ))}
        </div>

        <div style={{
          display: 'grid', gap: 8,
          gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
        }}>
          <NumField label="Lift kg" value={liftWeight} onChange={setLiftWeight} />
          <NumField label="Lifts/hr" value={liftFreq} onChange={setLiftFreq} />
          <NumField label="Reach cm" value={reach} onChange={setReach} />
          <NumField label="Seated %" value={seatedPct} onChange={setSeatedPct} />
        </div>

        <div style={{ marginTop: 12 }}>
          <Text strong style={{ fontSize: 12 }}>Risk method</Text>
          <Radio.Group value={method} onChange={(e) => setMethod(e.target.value)}
            style={{ marginLeft: 8 }}>
            <Radio.Button value="SIMPLE">Simple</Radio.Button>
            <Radio.Button value="RULA">RULA</Radio.Button>
            <Radio.Button value="REBA">REBA</Radio.Button>
            <Radio.Button value="OWAS">OWAS</Radio.Button>
          </Radio.Group>
        </div>

        <Space style={{ marginTop: 8 }}>
          <NumField label="Risk score" value={score} onChange={setScore} />
          <div>
            <Text strong style={{ fontSize: 12, display: 'block' }}>Risk band</Text>
            <Space>
              {RISK_BAND_OPTIONS.map((b) => (
                <Tag key={b.value} color={b.color}
                  style={{ cursor: 'pointer',
                           border: band === b.value ? '2px solid #1677ff' : undefined }}
                  onClick={() => setBand(band === b.value ? null : b.value)}>
                  {b.label}
                </Tag>
              ))}
            </Space>
          </div>
        </Space>

        <Form.Item label="Proposed action" style={{ marginTop: 12 }}>
          <Input.TextArea rows={2} value={proposed}
            onChange={(e) => setProposed(e.target.value)}
            placeholder="e.g. Raise trolley to waist height" />
        </Form.Item>
        <Form.Item label="Photo URL (optional)">
          <Input value={photoUrl ?? ''} onChange={(e) => setPhotoUrl(e.target.value || null)}
            placeholder="Paste after upload" />
        </Form.Item>
      </Form>
    </Modal>
  )
}

// ─── Shared mini-components ────────────────────────────────────────

function KPITile({ variant, value, label }: {
  variant: ThresholdResult; value: number | string; label: string
}) {
  return (
    <div className={`threshold-tile threshold-tile--${variant}`}>
      <div className="threshold-tile__value">{value}</div>
      <div className="threshold-tile__label">{label}</div>
    </div>
  )
}

function NumField({ label, value, onChange, suffix }: {
  label: string; value: number | null
  onChange: (v: number | null) => void; suffix?: string
}) {
  return (
    <div>
      <Text type="secondary" style={{ fontSize: 10, fontWeight: 600,
                                       letterSpacing: 0.5, display: 'block' }}>
        {label.toUpperCase()}{suffix ? ` (${suffix})` : ''}
      </Text>
      <InputNumber value={value} onChange={onChange}
        style={{ width: '100%' }}
        controls={false} inputMode="decimal" min={0} step={0.1} />
    </div>
  )
}
